import { AlertCircle } from 'lucide-react';
import { DeleteEncounterButton } from '../components/DeleteEncounterButton';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { EditorContent } from '@tiptap/react';
import { useNavigate, useParams } from '@tanstack/react-router';
import { forwardRef, type ReactNode, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import {
  ArrowLeft,
  ArrowUpRight,
  CheckCircle2,
  Clock,
  FileText,
  History,
  Loader2,
  Mic,
  Pencil,
  Play,
  Plus,
  Save,
  Search,
  Send,
  Sparkles,
  Stethoscope,
  Upload,
  User,
  XCircle,
} from 'lucide-react';

import { cn } from '@/lib/utils';
import { useAudioUpload } from '@/lib/hooks';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { hasUsableTranscript } from '../utils';
import { Button } from '@/components/ui/button';
import { useMembers } from '@/features/members';
import { useOrgDateFormat } from '@/lib/timezone';
import { QuoEncounterPanel } from '@/features/quo';
import { Separator } from '@/components/ui/separator';
import { usePregnancies } from '../api/pregnancies-api';
import { IconAction } from '@/components/ui/icon-action';
import { AudioBar } from '@/components/encounter/AudioBar';
import { P, RequirePermission } from '@/features/permissions';
import { HOME_CRUMB } from '@/components/layout/personalTrail';
import { formatCalendarDate, isoToDate } from '@/lib/utils/date';
import { RecordingsList } from '@/components/audio/RecordingsList';
import { audioDB, STALE_AUDIO_TTL_MS } from '@/lib/storage/audioDB';
import { ReferencePanel } from '@/components/encounter/ReferencePanel';
import { DocumentViewerSheet } from '../components/DocumentViewerSheet';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { useDocumentEditor } from '../components/editor/useDocumentEditor';
import type { RecordingsListRef } from '@/components/audio/RecordingsList';
import { DeleteRecordingButton } from '../components/DeleteRecordingButton';
import { usePageHeaderSetter } from '@/components/layout/PageHeaderContext';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { GenerateDocumentDialog } from '../components/GenerateDocumentDialog';
import { type TemplateSummary, usePickerTemplates } from '@/features/templates';
import { useEncounterSessionStore } from '@/lib/stores/encounter-session-store';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { type GeneratedDocumentOut, useActionExecutions, useWorkflowFailureDetails } from '../api/documents-api';
import { type PatientOut, useCreatePatient, usePatient, usePatients } from '../api/patients-api';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  encounterTypeBadgeStyles,
  encounterTypeLabels,
  encounterTypeToggleColors,
  documentStatusBadgeStyles as statusBadgeStyles,
} from '../constants';
import {
  useAssignEncounter,
  useDeleteAudioSegment,
  useEncounter,
  useEncounterAudioSegments,
  useEncounterDocuments,
  useEncounterRevisions,
  useUpdateEncounter,
  useUploadAudioSegment,
} from '../api/encounters-api';

const actionStatusConfig: Record<string, { icon: ReactNode; bg: string }> = {
  pending: {
    icon: <Clock className="h-4 w-4" />,
    bg: 'bg-warning-surface text-warning',
  },
  cancelled: {
    bg: 'bg-muted text-muted-foreground',
    icon: <XCircle className="h-4 w-4" />,
  },
  failed: {
    icon: <XCircle className="h-4 w-4" />,
    bg: 'bg-destructive-surface text-destructive',
  },
  completed: {
    bg: 'bg-success-surface text-success',
    icon: <CheckCircle2 className="h-4 w-4" />,
  },
  running: {
    bg: 'bg-info-surface text-info',
    icon: <Loader2 className="h-4 w-4 animate-spin" />,
  },
};

type ClinicalContext = {
  chief_complaint?: string;
  assessment?: string;
  plan?: string;
  vitals?: Record<string, string>;
  medications?: string[];
  lab_values?: Record<string, string>;
  template_id?: string;
  template_context?: Record<string, string>;
  context_view_template_id?: string;
};

// ─── Main Component ─────────────────────────────────────────────

interface DraftEditorHandle {
  save: () => void;
  submit: () => void;
  hasUnsavedChanges: () => boolean;
}

// ─── Header Stat ────────────────────────────────────────────────

interface DraftEditorProps {
  compact?: boolean;
  audioSegments: import('../api/encounters-api').AudioSegmentOut[];
  ctx: ClinicalContext | null;
  documents: GeneratedDocumentOut[];
  encounter: import('../api/encounters-api').EncounterOut;
  encounterId: string;
  onGenerateDocument: () => void;
  onSavingChange: (saving: 'save' | 'submit' | false) => void;
  onValidityChange: (canSubmit: boolean) => void;
  onViewDocument: (doc: GeneratedDocumentOut) => void;
  templates: TemplateSummary[];
  templatesMap: Map<string, TemplateSummary>;
}

// ─── Draft Editor ───────────────────────────────────────────────

export function EncounterDetailPage({
  encounterId: selectedId,
  onClose,
}: { encounterId?: string; onClose?: () => void } = {}) {
  const params = useParams({ strict: false }) as { encounterId?: string };
  const encounterId = selectedId ?? params.encounterId!;
  const embedded = !!onClose;
  const [confirmClose, setConfirmClose] = useState(false);
  const { recordingMeta } = useEncounterSessionStore();
  const navigate = useNavigate();
  const setPageHeader = usePageHeaderSetter();
  const [activeTab, setActiveTab] = useState('documents');
  const [viewingDoc, setViewingDoc] = useState<GeneratedDocumentOut | null>(null);
  const [showGenerateDialog, setShowGenerateDialog] = useState(false);
  const draftRef = useRef<DraftEditorHandle>(null);

  const { data: encounter, isLoading: loadingEncounter } = useEncounter(encounterId);
  const { data: patient, isLoading: loadingPatient } = usePatient(encounter?.patient_id ?? undefined);
  const { data: documents = [] } = useEncounterDocuments(encounterId, encounter?.status);
  const { data: revisions = [] } = useEncounterRevisions(encounterId);
  const { data: audioSegments = [] } = useEncounterAudioSegments(encounterId);
  const { data: actionsData } = useActionExecutions(encounterId ? { encounter_id: encounterId } : undefined);
  const { data: templatesData } = usePickerTemplates({ category: 'document' });

  const isDraft = encounter?.status === 'draft';
  const actions = actionsData?.data ?? [];
  const processingFailed = hasFailedProcessing(actions, documents);
  const templates = templatesData ?? [];
  const templatesMap = new Map(templates.map((t) => [t.id, t]));

  const meta = patient?.metadata_;
  const patientDisplayName = meta
    ? `${meta.last_name || ''}, ${meta.first_name || ''}`.replace(/^, |, $/g, '')
    : (patient?.medical_record_number ?? '');
  // Breadcrumb shows the same "First Last" form the member page titles itself with.
  const patientCrumbName = meta
    ? `${meta.first_name || ''} ${meta.last_name || ''}`.trim()
    : (patient?.medical_record_number ?? '');

  const [savingAction, setSavingAction] = useState<'save' | 'submit' | false>(false);
  const [canSubmit, setCanSubmit] = useState(false);

  const hasTranscript = hasUsableTranscript(encounter?.transcript);
  const primaryEncounterActions = isDraft ? (
    <div className="flex gap-2">
      <IconAction
        icon={Save}
        variant="outline"
        label="Save draft"
        disabled={savingAction !== false}
        onClick={() => draftRef.current?.save()}
      />
      <Button
        type="button"
        size="lg"
        className="min-h-12 min-w-32"
        disabled={savingAction !== false || !canSubmit}
        aria-label={savingAction === 'submit' ? 'Submitting encounter' : 'Submit encounter'}
        onClick={() => draftRef.current?.submit()}>
        {savingAction === 'submit' ? <Loader2 className="animate-spin" /> : <Send />}
        {savingAction === 'submit' ? 'Submitting…' : 'Submit'}
      </Button>
    </div>
  ) : encounter?.status === 'submitted' && processingFailed ? (
    <Badge variant="destructive">Processing failed</Badge>
  ) : encounter?.status === 'submitted' ? (
    <Badge variant="outline" className="gap-1.5 px-3 py-1.5 text-sm bg-info-surface text-info border-info-border">
      <Loader2 className="h-3.5 w-3.5 animate-spin" />
      Processing...
    </Badge>
  ) : (
    <RequirePermission permission={P.GENERATE_DOCUMENT}>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <span tabIndex={hasTranscript ? undefined : 0}>
              <Button size="sm" variant="outline" disabled={!hasTranscript} onClick={() => setShowGenerateDialog(true)}>
                <Sparkles className="mr-2 h-4 w-4" />
                Generate Document
              </Button>
            </span>
          </TooltipTrigger>
          {!hasTranscript && (
            <TooltipContent>No transcript available — record or add a transcript first.</TooltipContent>
          )}
        </Tooltip>
      </TooltipProvider>
    </RequirePermission>
  );
  const encounterActions = (
    <div className="flex items-center gap-2">
      {primaryEncounterActions}
      {encounter && (
        <DeleteEncounterButton
          encounter={encounter}
          disabled={savingAction !== false || recordingMeta.status === 'recording' || recordingMeta.status === 'paused'}
          onDeleted={() => {
            onClose?.();
            void navigate({ to: '/encounters' });
          }}
        />
      )}
    </div>
  );
  const closeEncounter = () => {
    if (
      savingAction ||
      recordingMeta.status === 'recording' ||
      recordingMeta.status === 'paused' ||
      recordingMeta.status === 'interrupted'
    ) {
      toast.info('Finish saving or stop recording before closing the encounter.');
      return;
    }
    if (draftRef.current?.hasUnsavedChanges()) setConfirmClose(true);
    else onClose?.();
  };

  // Depend on the id, not the object — refetches that return an identical
  // patient must not re-run the header effect.
  const patientId = patient?.id ?? encounter?.patient_id;

  useEffect(() => {
    if (embedded) return;
    if (!encounter) {
      // Loading: minimal personal trail so the header never briefly adapts
      // the route's static trail and exposes the professional-only
      // /encounters list as a crumb / back target.
      setPageHeader({ title: 'Encounter', breadcrumbs: [HOME_CRUMB, { title: 'Encounter' }] });
      return () => setPageHeader(null);
    }
    const title = `Encounter - ${formatCalendarDate(encounter.encounter_date)}`;
    setPageHeader({
      title,
      // Personal-edition trail: Home » member » this encounter. While the
      // patient is still loading the member crumb is omitted. The
      // professional header ignores page-provided trails and keeps the
      // route's static Encounters trail.
      breadcrumbs: [
        HOME_CRUMB,
        ...(patientId ? [{ href: `/patients/${patientId}`, title: patientCrumbName || 'Member' }] : []),
        { title },
      ],
      actions: encounterActions,
    });
    return () => setPageHeader(null);
  }, [
    encounter,
    setPageHeader,
    isDraft,
    savingAction,
    canSubmit,
    patientId,
    patientCrumbName,
    embedded,
    recordingMeta.status,
    navigate,
    onClose,
  ]);

  const wrapContent = (content: ReactNode) =>
    !embedded ? (
      content
    ) : (
      <>
        <Sheet
          open
          onOpenChange={(open) => {
            if (!open) closeEncounter();
          }}>
          <SheetContent side="right" className="w-full sm:max-w-5xl gap-0 overflow-hidden">
            <SheetHeader className="shrink-0 border-b pr-12">
              <SheetTitle>Encounter</SheetTitle>
              <SheetDescription>Review the encounter and prepare its reports.</SheetDescription>
              <div className="flex flex-wrap items-center gap-2">
                <Button variant="outline" size="sm" onClick={closeEncounter}>
                  <ArrowLeft />
                  Back to patient chart
                </Button>
                {encounterActions}
              </div>
            </SheetHeader>
            <div className="min-h-0 flex-1 overflow-y-scroll overscroll-contain" style={{ scrollbarGutter: 'stable' }}>
              {content}
            </div>
          </SheetContent>
        </Sheet>
        <AlertDialog open={confirmClose} onOpenChange={setConfirmClose}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Discard unsaved encounter changes?</AlertDialogTitle>
              <AlertDialogDescription>
                Save the draft before closing to keep your changes and uploaded recordings.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Keep editing</AlertDialogCancel>
              <AlertDialogAction onClick={() => onClose?.()}>Discard changes and close</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </>
    );

  if (loadingEncounter || loadingPatient) {
    return wrapContent(
      <div className="flex items-center justify-center p-16">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground/50" />
          <p className="text-sm text-muted-foreground">Loading encounter...</p>
        </div>
      </div>,
    );
  }

  if (!encounter) {
    return wrapContent(
      <div className="flex flex-col items-center justify-center h-64 gap-2">
        <FileText className="h-10 w-10 text-muted-foreground/40" />
        <p className="text-muted-foreground">Encounter not found.</p>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => (embedded ? closeEncounter() : navigate({ to: '/encounters' }))}>
          {embedded ? 'Back to patient chart' : 'Back to encounters'}
        </Button>
      </div>,
    );
  }

  const ctx = encounter.clinical_context as ClinicalContext | null;
  const encounterType = encounter.encounter_type ?? 'initial';

  return wrapContent(
    <div className="flex flex-col gap-6 p-4 md:p-6">
      {!embedded && patientId && (
        <Button
          variant="outline"
          className="w-fit"
          onClick={() => navigate({ to: '/patients/$patientId', params: { patientId } })}>
          <ArrowLeft />
          Back to patient chart
        </Button>
      )}
      {/* Where this encounter came from. Renders nothing unless a Quo call
          produced it, so it costs non-telephone encounters one empty query. */}
      <QuoEncounterPanel encounterId={encounter.id} />

      {/* Patient Section */}
      <PatientSection
        encounterId={encounterId}
        isDraft={isDraft}
        patient={patient}
        navigate={navigate}
        patientDisplayName={patientDisplayName}
      />

      {/* Encounter Header */}
      <div className="rounded-xl border bg-card">
        <div className="p-5 md:p-6">
          <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
            <div className="space-y-2">
              <h1 className="text-xl font-semibold tracking-tight md:text-2xl">
                {formatCalendarDate(encounter.encounter_date, 'EEEE, MMMM d, yyyy')}
              </h1>
              <div className="flex items-center gap-2 flex-wrap">
                {encounter.encounter_type && (
                  <Badge
                    variant="outline"
                    className={cn('text-xs font-medium', encounterTypeBadgeStyles[encounterType])}>
                    {encounterTypeLabels[encounterType] ?? encounterType}
                  </Badge>
                )}
                <Badge
                  variant="outline"
                  className={cn('text-xs font-medium capitalize', statusBadgeStyles[encounter.status])}>
                  {encounter.status}
                </Badge>
                {encounter.clinician && (
                  <>
                    <Separator className="h-4" orientation="vertical" />
                    <span className="flex items-center gap-1.5 text-sm text-muted-foreground">
                      <Stethoscope className="h-3.5 w-3.5" />
                      {encounter.clinician.name}
                    </span>
                  </>
                )}
              </div>
            </div>

            {/* Stats */}
            <div className="flex items-center gap-5">
              <HeaderStat label="Documents" value={documents.length} icon={<FileText className="h-4 w-4" />} />
              <HeaderStat label="Recordings" value={audioSegments.length} icon={<Mic className="h-4 w-4" />} />
              {revisions.length > 0 && (
                <HeaderStat
                  label="Revision"
                  value={revisions[0].revision_number}
                  icon={<History className="h-4 w-4" />}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      {isDraft ? (
        <DraftEditor
          compact={embedded}
          ref={draftRef}
          ctx={ctx}
          documents={documents}
          encounter={encounter}
          templates={templates}
          encounterId={encounterId}
          templatesMap={templatesMap}
          audioSegments={audioSegments}
          onViewDocument={setViewingDoc}
          onValidityChange={setCanSubmit}
          onSavingChange={setSavingAction}
          onGenerateDocument={() => setShowGenerateDialog(true)}
        />
      ) : (
        <ReadOnlyView
          compact={embedded}
          actions={actions}
          activeTab={activeTab}
          documents={documents}
          encounter={encounter}
          revisions={revisions}
          encounterId={encounterId}
          setActiveTab={setActiveTab}
          templatesMap={templatesMap}
          audioSegments={audioSegments}
          onViewDocument={setViewingDoc}
          onGenerateDocument={() => setShowGenerateDialog(true)}
        />
      )}

      <DocumentViewerSheet
        key={viewingDoc?.id ?? 'none'}
        open={!!viewingDoc}
        document={viewingDoc}
        temporal_url={viewingDoc?.temporal_url ?? null}
        onOpenChange={(open) => {
          if (!open) setViewingDoc(null);
        }}
      />

      <GenerateDocumentDialog
        encounterId={encounterId}
        open={showGenerateDialog}
        patientId={encounter?.patient_id ?? ''}
        onOpenChange={setShowGenerateDialog}
      />
    </div>,
  );
}

function HeaderStat({ icon, value, label }: { icon: ReactNode; value: number; label: string }) {
  return (
    <div className="flex items-center gap-2.5">
      <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-muted/60 text-muted-foreground">
        {icon}
      </div>
      <div>
        <p className="text-lg font-semibold leading-none tabular-nums">{value}</p>
        <p className="text-xs text-muted-foreground mt-0.5">{label}</p>
      </div>
    </div>
  );
}

const DraftEditor = forwardRef<DraftEditorHandle, DraftEditorProps>(function DraftEditor(
  {
    compact = false,
    ctx,
    encounter,
    documents,
    templates,
    encounterId,
    templatesMap,
    audioSegments,
    onSavingChange,
    onViewDocument,
    onValidityChange,
    onGenerateDocument,
  },
  ref,
) {
  const { formatInstant } = useOrgDateFormat();
  const recordingsListRef = useRef<RecordingsListRef>(null);
  const [recordingCount, setRecordingCount] = useState(0);
  const updateEncounter = useUpdateEncounter(encounterId);
  const uploadAudio = useUploadAudioSegment(encounterId);
  const deleteAudioSegment = useDeleteAudioSegment(encounterId);
  // Deletions are staged locally (draft semantics) — the segment is only
  // actually removed server-side when Save Draft / Submit flushes this set.
  const pendingDeletionsRef = useRef<Set<string>>(new Set());
  const { data: membersData } = useMembers();
  const activeMembers = (membersData ?? []).filter((m) => m.is_active);

  // Active pregnancy for the encounter's patient. Backend does not auto-link
  // pregnancy_id on draft creation, so fall back to the patient's active
  // pregnancy — otherwise OB flow-sheet misses PregnancySummary.
  const { data: pregnanciesData } = usePregnancies(encounter.patient_id ?? undefined);
  const activePregnancyId = pregnanciesData?.data?.find((p) => p.status === 'active')?.id ?? null;
  const effectivePregnancyId = encounter.pregnancy_id ?? activePregnancyId;

  const [isDragging, setIsDragging] = useState(false);

  const [encounterType, setEncounterType] = useState(encounter.encounter_type ?? '');
  const [encounterDate, setEncounterDate] = useState(encounter.encounter_date);
  const [assignedTo, setAssignedTo] = useState(encounter.assigned_to ?? '');
  const [selectedTemplateId, setSelectedTemplateId] = useState(ctx?.template_id ?? '');

  useEffect(() => {
    onValidityChange(!!selectedTemplateId && recordingCount > 0);
  }, [selectedTemplateId, recordingCount, onValidityChange]);

  const { data: contextViews } = usePickerTemplates({ category: 'context_view' });
  const contextViewTemplates = contextViews ?? [];
  // Restore the saved context view from the encounter; fall back to null so the
  // effect below can default it to General Visit on a truly fresh draft.
  const [contextViewId, setContextViewId] = useState<string | null>(ctx?.context_view_template_id ?? null);

  // Auto-select "General Visit" as default context view only if the encounter
  // didn't persist one and the user hasn't picked one yet.
  useEffect(() => {
    if (compact) return;
    if (contextViewId) return;
    if (ctx?.context_view_template_id) return;
    if (contextViewTemplates.length === 0) return;
    const generalView = contextViewTemplates.find((t) => t.name === 'General Visit');
    if (generalView) setContextViewId(generalView.id);
  }, [contextViewId, contextViewTemplates, ctx, compact]);

  const { recordingMeta } = useEncounterSessionStore();

  // On fresh mount with no active session, sweep STALE blobs from a prior
  // abandoned session — see QuickStartPage for the full rationale. The
  // TTL preserves blobs from the current tab's just-stopped recording
  // and from a parallel tab's active session, while clearing anything
  // old enough to be a leftover.
  const cleanupRanRef = useRef(false);
  useEffect(() => {
    if (cleanupRanRef.current) return;
    if (recordingMeta.status !== 'idle') return;
    cleanupRanRef.current = true;
    audioDB.deleteStale(STALE_AUDIO_TTL_MS).catch((err) => {
      console.error('Failed to prune stale audio recordings on mount', err);
    });
  }, [recordingMeta.status]);

  // Recover recordings from IndexedDB after the recorder pill finishes
  // (status === 'stopped' only — the prior 'idle' path leaked cross-session
  // blobs into this page; see PR #323 review).
  useEffect(() => {
    if (recordingMeta.status !== 'stopped') return;
    async function recoverRecordings() {
      const stored = await audioDB.getAll();
      if (stored.length === 0) return;
      const existing = recordingsListRef.current?.getRecordings() || [];
      const existingIds = new Set(existing.map((r) => r.id));
      for (const rec of stored) {
        if (!existingIds.has(rec.id)) {
          recordingsListRef.current?.addRecording({
            id: rec.id,
            blob: rec.blob,
            duration: rec.duration,
            timestamp: new Date(rec.createdAt),
            name: `Recording ${existing.length + 1}`,
          });
        }
      }
    }
    void recoverRecordings();
  }, [recordingMeta.status]);

  // Load existing audio segments into recordings list
  // Sync server audio segments into recordings list, keeping unsaved local recordings
  useEffect(() => {
    if (!recordingsListRef.current) return;
    const current = recordingsListRef.current.getRecordings();
    // Keep only unsaved local recordings (have blob, no segmentId)
    const unsaved = current.filter((r) => r.blob && !r.segmentId);
    // Build list from server segments
    const serverRecordings = audioSegments.map((seg) => ({
      id: seg.id,
      segmentId: seg.id,
      url: seg.audio_url,
      duration: seg.duration || 0,
      timestamp: new Date(seg.created_at),
      name: seg.audio_url.split('/').pop() || `Segment ${seg.order}`,
    }));
    recordingsListRef.current.setRecordings([...serverRecordings, ...unsaved]);
  }, [audioSegments]);

  const handleTemplateChange = useCallback((templateId: string) => {
    setSelectedTemplateId(templateId);
  }, []);

  // ─── Audio handlers ─────────────────────────────────────────

  const handleAudioUploadError = useCallback((error: string) => {
    toast.error(error);
  }, []);

  const handleAudioUploadSuccess = useCallback(
    (result: { blob: Blob; fileName: string; metadata: { duration: number } }) => {
      const newRecording = {
        blob: result.blob,
        timestamp: new Date(),
        name: result.fileName,
        id: crypto.randomUUID(),
        duration: result.metadata.duration,
      };
      recordingsListRef.current?.addRecording(newRecording);
    },
    [],
  );

  const audioUpload = useAudioUpload({
    maxFileSize: 100 * 1024 * 1024,
    onError: handleAudioUploadError,
    onSuccess: handleAudioUploadSuccess,
  });

  function buildClinicalContext(): Record<string, unknown> {
    return {
      ...ctx,
      template_id: selectedTemplateId || undefined,
      context_view_template_id: encounterType === 'telephone' ? undefined : contextViewId || undefined,
    };
  }

  // Upload any recordings still sitting locally (blob, no segmentId) — each
  // via direct-to-S3 + commit. Returns the names of any that failed; on
  // success the recording is swapped in-place for the committed server copy
  // so it isn't re-uploaded.
  const flushPendingRecordings = useCallback(async (): Promise<string[]> => {
    const recordings = recordingsListRef.current?.getRecordings() ?? [];
    const newRecordings = recordings.filter((r) => r.blob && !r.segmentId);
    const failed: string[] = [];
    for (let i = 0; i < newRecordings.length; i++) {
      const rec = newRecordings[i];
      if (!rec.blob) continue;
      try {
        const segment = await uploadAudio.mutateAsync({
          file: rec.blob,
          fileName: rec.name,
          duration: rec.duration,
          order: audioSegments.length + i,
        });
        recordingsListRef.current?.removeRecording(rec.id);
        recordingsListRef.current?.addRecording({
          ...rec,
          blob: undefined,
          segmentId: segment.id,
          url: segment.audio_url,
        });
      } catch {
        failed.push(rec.name);
      }
    }
    return failed;
  }, [uploadAudio, audioSegments.length]);

  // Actually deletes any recordings staged via onDeleteRecording. Failures
  // stay queued so a retry (next Save/Submit) will attempt them again.
  const flushPendingDeletions = useCallback(async (): Promise<void> => {
    const ids = Array.from(pendingDeletionsRef.current);
    for (const id of ids) {
      try {
        await deleteAudioSegment.mutateAsync(id);
        pendingDeletionsRef.current.delete(id);
      } catch {
        toast.error('Failed to delete a recording');
      }
    }
  }, [deleteAudioSegment]);

  const handleSave = useCallback(async () => {
    onSavingChange('save');
    try {
      await updateEncounter.mutateAsync({
        encounter_date: encounterDate,
        assigned_to: assignedTo || undefined,
        clinical_context: buildClinicalContext(),
        encounter_type: encounterType || undefined,
      });

      const failed = await flushPendingRecordings();
      failed.forEach((name) => toast.error(`Failed to upload ${name}`));
      await flushPendingDeletions();

      if (failed.length > 0) {
        toast.error('Recording upload incomplete. Keep this page open, save a copy, then retry Save.');
      } else {
        toast.success('Draft saved');
      }
    } catch {
      toast.error('Failed to save draft');
    } finally {
      onSavingChange(false);
    }
  }, [
    updateEncounter,
    flushPendingRecordings,
    flushPendingDeletions,
    encounterType,
    encounterDate,
    assignedTo,
    onSavingChange,
    selectedTemplateId,
    contextViewId,
  ]);

  const handleSubmit = useCallback(async () => {
    if (!selectedTemplateId) {
      toast.error('Select a template before submitting');
      return;
    }
    const recordings = recordingsListRef.current?.getRecordings() ?? [];
    if (recordings.length === 0) {
      toast.error('At least one recording is required');
      return;
    }

    onSavingChange('submit');
    try {
      // Recordings must land — and be committed — before the encounter
      // flips to 'submitted', since that status change kicks off the
      // document-generation workflow, which fetches audio segments
      // immediately. Abort the submit if any recording fails to upload
      // rather than starting generation with audio missing.
      const failed = await flushPendingRecordings();
      if (failed.length > 0) {
        toast.error(`Failed to upload ${failed.join(', ')} — submit cancelled. Keep this page open and save a copy before retrying.`);
        return;
      }
      await flushPendingDeletions();

      await updateEncounter.mutateAsync({
        status: 'submitted',
        encounter_date: encounterDate,
        assigned_to: assignedTo || undefined,
        clinical_context: buildClinicalContext(),
        encounter_type: encounterType || undefined,
      });
      // Recordings are now on the server; drop the local IndexedDB cache.
      await audioDB.deleteAll();
      toast.success('Encounter submitted');
    } catch (error) {
      const detail = (error as { response?: { data?: { detail?: unknown; message?: string } } }).response?.data;
      toast.error(
        typeof detail?.detail === 'string'
          ? detail.detail
          : detail?.message || 'Failed to submit encounter. Please try again.',
      );
    } finally {
      onSavingChange(false);
    }
  }, [
    flushPendingRecordings,
    flushPendingDeletions,
    updateEncounter,
    encounterType,
    encounterDate,
    assignedTo,
    onSavingChange,
    selectedTemplateId,
    contextViewId,
  ]);

  useImperativeHandle(
    ref,
    () => ({
      save: handleSave,
      submit: handleSubmit,
      hasUnsavedChanges: () =>
        encounterType !== (encounter.encounter_type ?? '') ||
        encounterDate !== encounter.encounter_date ||
        assignedTo !== (encounter.assigned_to ?? '') ||
        selectedTemplateId !== (ctx?.template_id ?? '') ||
        (!compact && contextViewId !== (ctx?.context_view_template_id ?? null)) ||
        pendingDeletionsRef.current.size > 0 ||
        !!recordingsListRef.current?.getRecordings().some((r) => r.blob && !r.segmentId),
    }),
    [
      handleSave,
      handleSubmit,
      encounter,
      encounterType,
      encounterDate,
      assignedTo,
      selectedTemplateId,
      ctx,
      compact,
      contextViewId,
    ],
  );

  const isTelephone = encounterType === 'telephone';
  const audioPanel = (
    <Card className="border-border/60">
      <CardHeader className="pb-4">
        <CardTitle className="text-base flex items-center gap-2">
          <div className="flex h-7 w-7 items-center justify-center rounded-md bg-primary/10 text-primary">
            <Mic className="h-3.5 w-3.5" />
          </div>
          Audio Files
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <input
          id="draft-audio-file-input"
          type="file"
          accept="audio/*"
          className="hidden"
          onChange={async (e) => {
            const file = e.target.files?.[0];
            if (file) await audioUpload.upload(file);
          }}
        />
        <label
          htmlFor="draft-audio-file-input"
          onDragOver={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          onDragEnter={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setIsDragging(true);
          }}
          onDragLeave={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setIsDragging(false);
          }}
          onDrop={async (e) => {
            e.preventDefault();
            e.stopPropagation();
            setIsDragging(false);
            const file = e.dataTransfer.files?.[0];
            if (file && file.type.startsWith('audio/')) {
              await audioUpload.upload(file);
            } else if (file) {
              toast.error('Please upload an audio file');
            }
          }}>
          <div
            className={cn(
              'flex flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed p-6 transition-all cursor-pointer',
              isDragging && 'border-primary bg-primary/5 scale-[1.01]',
              !isDragging && 'border-muted-foreground/20 hover:border-primary/40 hover:bg-muted/30',
            )}>
            <Upload
              className={cn('h-5 w-5 transition-colors', isDragging ? 'text-primary' : 'text-muted-foreground')}
            />
            <div className="text-center space-y-0.5">
              <p className="text-sm font-medium">{isDragging ? 'Drop audio file here' : 'Upload audio file'}</p>
              <p className="text-xs text-muted-foreground">Drag & drop or click to browse</p>
            </div>
          </div>
        </label>

        <RecordingsList
          ref={recordingsListRef}
          onRecordingsChange={setRecordingCount}
          onDeleteRecording={(recording) => {
            if (recording.segmentId == null) return;
            // Staged only — actually deleted server-side on Save Draft/Submit
            // (flushPendingDeletions), consistent with draft semantics.
            pendingDeletionsRef.current.add(recording.segmentId);
          }}
        />
      </CardContent>
    </Card>
  );

  return (
    <div className="flex flex-col gap-6">
      {/* AudioBar (replaces inline audio section) */}
      {!isTelephone && (
        <AudioBar
          onRecordingStopped={(blob, duration) => {
            if (duration === 0) return;
            const current = recordingsListRef.current?.getRecordings() || [];
            recordingsListRef.current?.addRecording({
              blob,
              duration,
              timestamp: new Date(),
              id: crypto.randomUUID(),
              name: `Recording ${current.length + 1}`,
            });
          }}
        />
      )}

      <div className={cn('grid gap-6', !compact && 'lg:grid-cols-2')}>
        {/* Left: Clinical Context */}
        <div className="min-w-0 space-y-6">
          {/* Context View selector */}
          {!compact && !isTelephone && (
            <div className="flex items-center gap-2">
              <Label className="text-sm text-muted-foreground">Context View:</Label>
              <Select value={contextViewId ?? ''} onValueChange={(v) => setContextViewId(v || null)}>
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder="Select view..." />
                </SelectTrigger>
                <SelectContent>
                  {contextViewTemplates.map((t) => (
                    <SelectItem key={t.id} value={t.id}>
                      {t.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Encounter Details */}
          <Card className="border-border/60">
            <CardHeader className="pb-4">
              <CardTitle className="text-base flex items-center gap-2">
                <div className="flex h-7 w-7 items-center justify-center rounded-md bg-primary/10 text-primary">
                  <Pencil className="h-3.5 w-3.5" />
                </div>
                Encounter Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="space-y-2">
                <Label className="text-sm">Encounter Type</Label>
                <ToggleGroup
                  type="single"
                  variant="outline"
                  value={encounterType}
                  className="w-full flex-wrap"
                  onValueChange={(value) => {
                    if (value) setEncounterType(value);
                  }}>
                  {(['initial', 'follow-up', 'urgent', 'telehealth', 'telephone'] as const).map((type) => (
                    <ToggleGroupItem
                      key={type}
                      value={type}
                      className={cn('capitalize text-sm', encounterTypeToggleColors[type])}>
                      {type === 'telephone' ? 'Phone call' : type}
                    </ToggleGroupItem>
                  ))}
                </ToggleGroup>
              </div>

              <div className="grid gap-5 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label className="text-sm">Encounter Date</Label>
                  <Input type="date" value={encounterDate} onChange={(e) => setEncounterDate(e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm">Assigned Clinician</Label>
                  <Select value={assignedTo} onValueChange={setAssignedTo}>
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select clinician..." />
                    </SelectTrigger>
                    <SelectContent>
                      {activeMembers.map((member) => (
                        <SelectItem key={member.user_id} value={member.user_id}>
                          {member.user_name ?? member.user_email ?? 'Unknown'}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Template Selection */}
          <Card className="border-border/60">
            <CardHeader className="pb-4">
              <CardTitle className="text-base flex items-center gap-2">
                <div className="flex h-7 w-7 items-center justify-center rounded-md bg-primary/10 text-primary">
                  <Sparkles className="h-3.5 w-3.5" />
                </div>
                Template & Generation
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label className="text-sm">Template</Label>
                <Select
                  value={selectedTemplateId || '__none__'}
                  onValueChange={(v) => handleTemplateChange(v === '__none__' ? '' : v)}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select template..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__" className="text-muted-foreground">
                      No template
                    </SelectItem>
                    {templates
                      .filter((t) => t.is_active)
                      .map((t) => (
                        <SelectItem key={t.id} value={t.id}>
                          {t.name}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>

              {encounter.status !== 'draft' && (
                <RequirePermission permission={P.GENERATE_DOCUMENT}>
                  <Button variant="outline" className="w-full" onClick={onGenerateDocument}>
                    <Sparkles className="mr-2 h-4 w-4" />
                    Generate Document
                  </Button>
                </RequirePermission>
              )}
            </CardContent>
          </Card>

          {audioPanel}

          {documents.length > 0 && (
            <Card className="border-border/60">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium flex items-center justify-between">
                  <span>Generated Documents</span>
                  <Badge className="text-xs" variant="secondary">
                    {documents.length}
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {documents.map((doc) => {
                  const tmpl = doc.template_id ? templatesMap.get(doc.template_id) : undefined;
                  return (
                    <div
                      key={doc.id}
                      className="flex items-center gap-3 rounded-lg border border-border/60 p-3 cursor-pointer hover:bg-muted/40 transition-colors"
                      onClick={() => onViewDocument(doc)}>
                      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
                        <FileText className="h-3.5 w-3.5" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium truncate">{tmpl?.name || 'Document'}</p>
                        <p className="text-xs text-muted-foreground">
                          {formatInstant(doc.generated_at, 'MMM d, h:mm a')}
                        </p>
                      </div>
                      <Badge variant="outline" className={cn('text-[10px] shrink-0', statusBadgeStyles[doc.status])}>
                        {doc.status}
                      </Badge>
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Right: Reference Panel */}
        {!compact && !isTelephone && (
          <div className="min-w-0 min-h-[400px] lg:sticky lg:top-4 lg:self-start">
            <ReferencePanel
              currentEncounterId={encounterId}
              pregnancyId={effectivePregnancyId}
              contextViewTemplateId={contextViewId}
              patientId={encounter.patient_id ?? ''}
            />
          </div>
        )}
      </div>
    </div>
  );
});

// ─── Read-Only View ─────────────────────────────────────────────

function CreatePatientDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const createPatient = useCreatePatient();

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [mrn, setMrn] = useState('');
  const [dob, setDob] = useState('');
  const [gender, setGender] = useState('');

  function reset() {
    setFirstName('');
    setLastName('');
    setMrn('');
    setDob('');
    setGender('');
  }

  async function handleCreate() {
    if (!firstName.trim() || !lastName.trim() || !mrn.trim()) {
      toast.error('First name, last name, and MRN are required');
      return;
    }

    try {
      await createPatient.mutateAsync({
        medical_record_number: mrn.trim(),
        metadata_: {
          last_name: lastName.trim(),
          gender: gender || undefined,
          first_name: firstName.trim(),
          date_of_birth: dob || undefined,
        },
      });
      toast.success(`Patient ${lastName}, ${firstName} created`);
      reset();
      onOpenChange(false);
    } catch {
      toast.error('Failed to create patient');
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Create New Patient</DialogTitle>
          <DialogDescription>Add a new patient record. You can add more details later.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-sm">
                First Name <span className="text-destructive">*</span>
              </Label>
              <Input value={firstName} placeholder="John" onChange={(e) => setFirstName(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm">
                Last Name <span className="text-destructive">*</span>
              </Label>
              <Input value={lastName} placeholder="Smith" onChange={(e) => setLastName(e.target.value)} />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-sm">
              Medical Record Number <span className="text-destructive">*</span>
            </Label>
            <Input value={mrn} placeholder="MRN-00001" onChange={(e) => setMrn(e.target.value)} />
          </div>

          <div className="space-y-1.5">
            <Label className="text-sm">Date of Birth</Label>
            <Input type="date" value={dob} onChange={(e) => setDob(e.target.value)} />
          </div>

          <div className="space-y-2">
            <Label className="text-sm">Gender</Label>
            <RadioGroup value={gender} className="flex gap-4" onValueChange={setGender}>
              {(
                [
                  ['M', 'Male'],
                  ['F', 'Female'],
                  ['O', 'Other'],
                ] as const
              ).map(([value, label]) => (
                <label key={value} className="flex items-center gap-2 cursor-pointer">
                  <RadioGroupItem value={value} />
                  <span className="text-sm">{label}</span>
                </label>
              ))}
            </RadioGroup>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button disabled={createPatient.isPending} onClick={handleCreate}>
            {createPatient.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Create Patient
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Transcript Viewer (read-only Tiptap) ───────────────────────

function EmptyState({
  icon,
  action,
  message,
  description,
}: {
  icon: ReactNode;
  message: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center justify-center py-12 px-6 text-center">
      <div className="text-muted-foreground/30 mb-3">{icon}</div>
      <p className="font-medium text-muted-foreground">{message}</p>
      {description && <p className="text-sm text-muted-foreground/70 mt-1 max-w-xs">{description}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

// ─── Helpers ────────────────────────────────────────────────────

function PatientSection({
  encounterId,
  compact = false,
  patient,
  isDraft,
  navigate,
  patientDisplayName,
}: {
  encounterId: string;
  compact?: boolean;
  patient: PatientOut | undefined;
  patientDisplayName: string;
  isDraft: boolean;
  navigate: ReturnType<typeof useNavigate>;
}) {
  const assignment = useAssignEncounter();
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);

  const { data: patientsData } = usePatients(popoverOpen ? { limit: 8, search: search || undefined } : undefined);
  const patients = patientsData?.data ?? [];

  const meta = patient?.metadata_;
  // isoToDate, not new Date(): a date-only string parsed as UTC midnight renders
  // a day early under a negative offset.
  const dobDate = isoToDate(meta?.date_of_birth ?? '');
  const dob = dobDate ? format(dobDate, 'MMM d, yyyy') : null;
  const gender = meta?.gender === 'M' ? 'Male' : meta?.gender === 'F' ? 'Female' : (meta?.gender ?? null);

  return (
    <>
      <div className="flex items-center gap-3 rounded-xl border border-border/60 bg-card px-4 py-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-muted">
          <User className="h-4.5 w-4.5 text-muted-foreground" />
        </div>

        <div className="flex-1 min-w-0">
          {patient ? (
            <>
              <p className="text-xl font-semibold tracking-tight md:text-2xl">{patientDisplayName}</p>
              <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5">
                <span>MRN: {patient.medical_record_number}</span>
                {dob && (
                  <>
                    <span className="text-border">&middot;</span>
                    <span>DOB: {dob}</span>
                  </>
                )}
                {gender && (
                  <>
                    <span className="text-border">&middot;</span>
                    <span>{gender}</span>
                  </>
                )}
              </div>
            </>
          ) : (
            <p className="text-sm text-muted-foreground">No patient selected</p>
          )}
        </div>

        {compact ? null : isDraft || !patient ? (
          <RequirePermission permission={P.UPDATE_ENCOUNTER}>
            <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
              <PopoverTrigger asChild>
                <Button size="sm" variant="outline" className="shrink-0">
                  <Search className="mr-1.5 h-3.5 w-3.5" />
                  {patient ? 'Change' : 'Select Patient'}
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-80 p-0">
                <div className="p-3 border-b border-border/60">
                  <div className="relative">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                    <Input
                      value={search}
                      className="h-8 pl-8 text-sm"
                      placeholder="Search by name or MRN..."
                      onChange={(e) => setSearch(e.target.value)}
                    />
                  </div>
                </div>
                <div className="max-h-56 overflow-y-auto p-1">
                  {patients.length === 0 ? (
                    <p className="text-xs text-muted-foreground text-center py-6">
                      {search ? 'No patients found' : 'Start typing to search...'}
                    </p>
                  ) : (
                    patients.map((p) => {
                      const m = p.metadata_;
                      const name = m
                        ? `${m.last_name || ''}, ${m.first_name || ''}`.replace(/^, |, $/g, '')
                        : p.medical_record_number;
                      const isSelected = patient?.id === p.id;
                      return (
                        <button
                          key={p.id}
                          disabled={assignment.isPending}
                          onClick={async () => {
                            try {
                              if (!isSelected) await assignment.mutateAsync({ encounterId, patient_id: p.id });
                              setPopoverOpen(false);
                              setSearch('');
                              toast.success('Encounter and reports assigned to patient');
                            } catch {
                              toast.error('Could not assign this encounter. Please try again.');
                            }
                          }}
                          className={cn(
                            'w-full flex items-center gap-2.5 rounded-lg p-2 text-left text-sm transition-colors',
                            isSelected ? 'bg-primary/10 text-primary' : 'hover:bg-muted',
                          )}>
                          <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground">
                            <User className="h-3 w-3" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="font-medium truncate text-sm">{name}</p>
                            <p className="text-[11px] text-muted-foreground">MRN: {p.medical_record_number}</p>
                          </div>
                          {isSelected && <CheckCircle2 className="h-4 w-4 text-primary shrink-0" />}
                        </button>
                      );
                    })
                  )}
                </div>
                <div className="border-t border-border/60 p-1.5">
                  <button
                    className="w-full flex items-center gap-2 rounded-lg p-2 text-sm text-primary hover:bg-primary/5 transition-colors"
                    onClick={() => {
                      setPopoverOpen(false);
                      setSearch('');
                      setCreateOpen(true);
                    }}>
                    <Plus className="h-4 w-4" />
                    Create New Patient
                  </button>
                </div>
              </PopoverContent>
            </Popover>
          </RequirePermission>
        ) : patient ? (
          <Button
            size="sm"
            variant="ghost"
            className="shrink-0 text-muted-foreground hover:text-foreground"
            onClick={() => navigate({ to: '/patients/$patientId', params: { patientId: patient.id } })}>
            View Profile
            <ArrowUpRight className="ml-1 h-3.5 w-3.5" />
          </Button>
        ) : null}
      </div>

      <CreatePatientDialog open={createOpen} onOpenChange={setCreateOpen} />
    </>
  );
}

// ─── Patient Section ────────────────────────────────────────────

function ReadOnlyView({
  compact = false,
  actions,
  encounter,
  activeTab,
  documents,
  revisions,
  encounterId,
  setActiveTab,
  templatesMap,
  audioSegments,
  onViewDocument,
  onGenerateDocument,
}: {
  compact?: boolean;
  encounter: import('../api/encounters-api').EncounterOut;
  activeTab: string;
  setActiveTab: (tab: string) => void;
  documents: GeneratedDocumentOut[];
  revisions: import('../api/encounters-api').EncounterRevisionOut[];
  audioSegments: import('../api/encounters-api').AudioSegmentOut[];
  actions: import('../api/documents-api').ActionExecutionOut[];
  templatesMap: Map<string, TemplateSummary>;
  onGenerateDocument: () => void;
  onViewDocument: (doc: GeneratedDocumentOut) => void;
  encounterId: string;
}) {
  const { formatInstant } = useOrgDateFormat();
  const processingFailed = hasFailedProcessing(actions, documents);
  // Active pregnancy fallback when the encounter lacks a stored pregnancy_id.
  const { data: pregnanciesData } = usePregnancies(encounter.patient_id ?? undefined);
  const activePregnancyId = pregnanciesData?.data?.find((p) => p.status === 'active')?.id ?? null;
  const effectivePregnancyId = encounter.pregnancy_id ?? activePregnancyId;

  return (
    <div className={cn('grid gap-6', !compact && 'lg:grid-cols-[1fr_360px]')}>
      {/* Left: Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="w-full justify-start bg-muted/40">
          <TabsTrigger value="documents" className="gap-1.5">
            <Sparkles className="h-3.5 w-3.5" />
            Documents
            {documents.length > 0 && (
              <Badge variant="secondary" className="ml-1 h-5 min-w-5 px-1.5 text-[10px] rounded-full">
                {documents.length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="transcript" className="gap-1.5">
            <FileText className="h-3.5 w-3.5" />
            Transcript
          </TabsTrigger>
          <TabsTrigger value="audio" className="gap-1.5">
            <Mic className="h-3.5 w-3.5" />
            Audio
          </TabsTrigger>
          <TabsTrigger value="revisions" className="gap-1.5">
            <History className="h-3.5 w-3.5" />
            Revisions
            {revisions.length > 0 && (
              <Badge variant="secondary" className="ml-1 h-5 min-w-5 px-1.5 text-[10px] rounded-full">
                {revisions.length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="actions">Processing history</TabsTrigger>
        </TabsList>

        {/* Transcript Tab */}
        <TabsContent className="mt-4" value="transcript">
          <Card className="border-border/60">
            <CardContent className="p-0">
              {encounter.transcript ? (
                <TranscriptViewer content={encounter.transcript} />
              ) : (
                <div className="p-5 md:p-6">
                  <EmptyState
                    message={
                      processingFailed
                        ? 'Transcription or report processing failed'
                        : encounter.status === 'submitted'
                          ? 'Transcript is being processed...'
                          : 'No transcript available'
                    }
                    description={
                      processingFailed
                        ? 'No transcript was saved. Check Processing history for the recorded failure.'
                        : encounter.status === 'submitted'
                          ? 'Audio is being transcribed. This may take a few minutes.'
                          : "This encounter doesn't have a transcript yet."
                    }
                    icon={
                      encounter.status === 'submitted' && !processingFailed ? (
                        <Loader2 className="h-10 w-10 animate-spin" />
                      ) : (
                        <FileText className="h-10 w-10" />
                      )
                    }
                  />
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Documents Tab */}
        <TabsContent className="mt-4" value="documents">
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                {documents.length} document{documents.length !== 1 ? 's' : ''} generated
              </p>
              {encounter.status !== 'submitted' && (
                <RequirePermission permission={P.GENERATE_DOCUMENT}>
                  <Button size="sm" variant="outline" onClick={onGenerateDocument}>
                    <Sparkles className="mr-2 h-3.5 w-3.5" />
                    Generate New
                  </Button>
                </RequirePermission>
              )}
            </div>

            {documents.length === 0 ? (
              <Card className="border-border/60">
                <CardContent className="p-0">
                  {processingFailed ? (
                    <EmptyState message="Processing failed" icon={<AlertCircle className="h-10 w-10" />} description="No report was generated. Review Processing history for details. Use Encounter actions to delete this unfinished encounter, or generate a new report." />
                  ) : encounter.status === 'submitted' ? (
                    <EmptyState
                      message="Document is being generated..."
                      icon={<Loader2 className="h-10 w-10 animate-spin" />}
                      description="The transcript is being processed and a document will be created automatically."
                    />
                  ) : (
                    <EmptyState
                      message="No documents yet"
                      icon={<FileText className="h-10 w-10" />}
                      description="Generate an AI-powered document from this encounter."
                      action={
                        <RequirePermission permission={P.GENERATE_DOCUMENT}>
                          <Button size="sm" variant="outline" onClick={onGenerateDocument}>
                            <Sparkles className="mr-2 h-3.5 w-3.5" />
                            Generate Document
                          </Button>
                        </RequirePermission>
                      }
                    />
                  )}
                </CardContent>
              </Card>
            ) : (
              documents.map((doc) => {
                const tmpl = doc.template_id ? templatesMap.get(doc.template_id) : undefined;
                return (
                  <div
                    key={doc.id}
                    className="group rounded-xl border border-border/60 bg-card p-4 cursor-pointer hover:border-primary/30 hover:shadow-sm transition-all"
                    onClick={() => onViewDocument(doc)}>
                    <div className="flex items-start gap-3">
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                        <FileText className="h-5 w-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-sm">{tmpl?.name || 'Document'}</p>
                          <Badge variant="outline" className={cn('text-[10px]', statusBadgeStyles[doc.status])}>
                            {doc.status}
                          </Badge>
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {formatInstant(doc.generated_at, "MMM d, yyyy 'at' h:mm a")}
                        </p>
                        <p className="text-sm text-muted-foreground/80 mt-2 line-clamp-2 leading-relaxed">
                          {doc.content.slice(0, 140).replace(/[#*]/g, '')}...
                        </p>
                      </div>
                      <ArrowUpRight className="h-4 w-4 text-muted-foreground/40 group-hover:text-primary transition-colors shrink-0 mt-0.5" />
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </TabsContent>

        {/* Audio Tab */}
        <TabsContent value="audio" className="mt-4">
          <div className="space-y-3">
            {audioSegments.length === 0 ? (
              <Card className="border-border/60">
                <CardContent className="p-0">
                  <EmptyState
                    message="No audio recordings"
                    icon={<Mic className="h-10 w-10" />}
                    description="This encounter doesn't have any audio segments."
                  />
                </CardContent>
              </Card>
            ) : (
              audioSegments.map((seg) => (
                <div key={seg.id} className="rounded-xl border border-border/60 bg-card p-4">
                  <div className="flex items-center gap-4">
                    <Button
                      size="icon"
                      variant="outline"
                      className="h-10 w-10 shrink-0 rounded-full hover:bg-primary hover:text-primary-foreground hover:border-primary transition-colors">
                      <Play className="h-4 w-4" />
                    </Button>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <p className="font-medium text-sm">Segment {seg.order}</p>
                        <span className="text-xs text-muted-foreground tabular-nums font-medium">
                          {seg.duration
                            ? `${Math.floor(seg.duration / 60)}:${(seg.duration % 60).toString().padStart(2, '0')}`
                            : '--:--'}
                        </span>
                      </div>
                      <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                        <div className="h-full w-0 rounded-full bg-primary transition-all" />
                      </div>
                      <p className="text-xs text-muted-foreground mt-1.5">
                        {formatInstant(seg.created_at, 'MMM d, yyyy h:mm a')}
                      </p>
                    </div>
                  </div>
                  <div className="mt-3">
                    <DeleteRecordingButton segment={seg} encounterId={encounterId} />
                  </div>
                  {seg.transcription && (
                    <Accordion collapsible type="single" className="mt-3">
                      <AccordionItem value="transcription" className="border rounded-lg border-border/40 bg-muted/40">
                        <AccordionTrigger className="px-3 py-2 text-xs font-medium text-muted-foreground hover:no-underline">
                          <div className="flex items-center gap-1.5">
                            <FileText className="h-3.5 w-3.5" />
                            Transcription ({seg.transcription.split(/\s+/).length} words)
                          </div>
                        </AccordionTrigger>
                        <AccordionContent className="px-3 pb-3">
                          <TranscriptViewer content={seg.transcription} />
                        </AccordionContent>
                      </AccordionItem>
                    </Accordion>
                  )}
                </div>
              ))
            )}
          </div>
        </TabsContent>

        {/* Revisions Tab */}
        <TabsContent className="mt-4" value="revisions">
          {revisions.length === 0 ? (
            <Card className="border-border/60">
              <CardContent className="p-0">
                <EmptyState
                  message="No revision history"
                  icon={<History className="h-10 w-10" />}
                  description="Changes to this encounter will be tracked here."
                />
              </CardContent>
            </Card>
          ) : (
            <div className="relative space-y-0">
              {revisions.map((rev, i) => (
                <div key={rev.id} className="relative pl-8 pb-6 last:pb-0">
                  {/* Timeline connector */}
                  {i < revisions.length - 1 && <div className="absolute left-[13px] top-8 bottom-0 w-px bg-border" />}
                  {/* Timeline dot */}
                  <div className="absolute left-0 top-1 flex h-7 w-7 items-center justify-center rounded-full border-2 border-border bg-background">
                    <span className="text-[10px] font-bold tabular-nums">{rev.revision_number}</span>
                  </div>
                  {/* Content */}
                  <div className="rounded-xl border border-border/60 bg-card overflow-hidden">
                    <div className="flex items-center justify-between px-4 py-3 bg-muted/30 border-b border-border/40">
                      <p className="text-sm font-medium">Revision {rev.revision_number}</p>
                      <span className="text-xs text-muted-foreground">
                        {formatInstant(rev.created_at, 'MMM d, yyyy h:mm a')}
                      </span>
                    </div>
                    <div className="p-4">
                      <p className="text-sm leading-relaxed text-foreground/80 max-h-32 overflow-y-auto">
                        {rev.transcript.slice(0, 400)}
                        {rev.transcript.length > 400 && <span className="text-muted-foreground">...</span>}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Processing history, including recorded workflow failures. */}
        <TabsContent value="actions" className="mt-4">
          <div className="space-y-3">
            {actions.length === 0 ? (
              <Card className="border-border/60">
                <CardContent className="p-0">
                  <EmptyState
                    message="No workflow actions"
                    icon={<Send className="h-10 w-10" />}
                    description="Actions like document generation and EMR exports will appear here."
                  />
                </CardContent>
              </Card>
            ) : (
              actions.map((action) => {
                const tmpl = action.template_id ? templatesMap.get(action.template_id) : undefined;
                const config = actionStatusConfig[action.status] ?? {
                  icon: <Clock className="h-4 w-4" />,
                  bg: 'bg-muted text-muted-foreground',
                };
                return (
                  <div key={action.id} className="rounded-xl border border-border/60 bg-card p-4">
                    <div className="flex items-start gap-3">
                      <div className={cn('flex h-9 w-9 shrink-0 items-center justify-center rounded-full', config.bg)}>
                        {config.icon}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-medium text-sm">{action.action.replace(/_/g, ' ')}</p>
                          <Badge
                            variant="outline"
                            className={cn('text-[10px] capitalize', statusBadgeStyles[action.status])}>
                            {action.status}
                          </Badge>
                        </div>
                        <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1.5 text-xs text-muted-foreground">
                          {tmpl && (
                            <span className="flex items-center gap-1">
                              <FileText className="h-3 w-3" />
                              {tmpl.name}
                            </span>
                          )}
                          {action.started_at && (
                            <span className="flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              {formatInstant(action.started_at, 'MMM d, h:mm a')}
                            </span>
                          )}
                          {action.completed_at && action.started_at && (
                            <span className="tabular-nums">
                              {Math.round(
                                (new Date(action.completed_at).getTime() - new Date(action.started_at).getTime()) /
                                  1000,
                              )}
                              s duration
                            </span>
                          )}
                        </div>
                        {action.status === 'failed' && (
                          <WorkflowFailureDetails key={action.id} executionId={action.id} />
                        )}
                        {action.result && (
                          <div className="mt-3 rounded-lg bg-muted/40 p-3 border border-border/40">
                            <pre className="text-xs font-mono whitespace-pre-wrap break-all">
                              {JSON.stringify(action.result, null, 2)}
                            </pre>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Right: Reference Panel */}
      {!compact && (
        <div className="min-w-0 min-h-[400px] lg:sticky lg:top-4 lg:self-start">
          <ReferencePanel
            currentEncounterId={encounterId}
            pregnancyId={effectivePregnancyId}
            patientId={encounter.patient_id ?? ''}
            contextViewTemplateId={
              (
                encounter.clinical_context as {
                  context_view_template_id?: string;
                } | null
              )?.context_view_template_id ?? null
            }
          />
        </div>
      )}
    </div>
  );
}

// ─── Create Patient Dialog ──────────────────────────────────────

function TranscriptViewer({ content }: { content: string }) {
  const editor = useDocumentEditor({ editable: false, initialContent: content });
  return <EditorContent editor={editor} />;
}

function hasFailedProcessing(
  actions: import('../api/documents-api').ActionExecutionOut[],
  documents: GeneratedDocumentOut[],
) {
  if (actions.some((action) => ['pending', 'running'].includes(action.status))) return false;
  const latest = [...actions].sort((a, b) => b.created_at.localeCompare(a.created_at))[0];
  if (latest) return ['failed', 'cancelled'].includes(latest.status);
  return documents.some((doc) => doc.status === 'failed') && !documents.some((doc) => doc.status === 'generating');
}

function WorkflowFailureDetails({ executionId }: { executionId: string }) {
  const details = useWorkflowFailureDetails();
  return (
    <div className="mt-3 flex flex-col gap-2">
      <Button variant="outline" disabled={details.isPending} onClick={() => details.mutate(executionId)}>
        {details.isPending ? 'Loading failure details…' : 'Load failure details'}
      </Button>
      {details.isError && <p role="alert">Could not load failure details. Try again.</p>}
      {details.data && <p>{details.data.message}</p>}
      {details.data?.status === 'available' && details.data.failures.length === 0 && (
        <p>No failure events remain in this run’s history.</p>
      )}
      {details.data?.failures.map((failure) => (
        <div key={failure.event_id}>
          <p className="font-medium">
            {failure.step} (event {failure.event_id})
          </p>
          <pre className="whitespace-pre-wrap break-words text-xs">{failure.error}</pre>
        </div>
      ))}
    </div>
  );
}
